import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
  Slide,
  AppBar,
  Toolbar,
  Pagination,
  Alert,
  Snackbar,
  List,
} from '@mui/material';
import { Close, Add, ArrowBack } from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TriggerListItem } from '../TriggerListItem';
import { TriggerForm } from '../TriggerForm';
import { ConfirmDeleteModal } from '../ConfirmDeleteModal';
import {
  UserNotificationTrigger,
  UserNotificationTriggerFormData,
} from '../../types';
import {
  useCreateUserNotificationTrigger,
  useDeleteUserNotificationTrigger,
  useGetUserNotificationTriggerCapabilities,
  useGetUserNotificationTriggers,
  useUpdateUserNotificationTrigger,
  useToggleUserNotificationTrigger,
} from '../../queries';
import { usePagination } from '@frontend/features/common/components/Table/usePagination';
import { QueryKeys } from '@frontend/features/common/types';
import { useQueryClient } from 'react-query';
import { PagedResponse } from '@bandido/shared/dtos';

interface UserNotificationTriggersModalProps {
  open: boolean;
  onClose: () => void;
}

// Transition para mobile
const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});



export const UserNotificationTriggersModal = ({ open, onClose }: UserNotificationTriggersModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [editingTrigger, setEditingTrigger] = useState<UserNotificationTrigger | null>(null);
  const [deletingTrigger, setDeletingTrigger] = useState<UserNotificationTrigger | null>(null);
  const [toggleLoadingId, setToggleLoadingId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const { page: currentPage, rowsPerPage: pageSize, handlePagination } = usePagination(
    undefined, 
    undefined, 
    { keepPaginationInUrl: false }
  );

  const { data: deliveryCapabilities } =
    useGetUserNotificationTriggerCapabilities(open);

  const { data: triggersData, isLoading } = useGetUserNotificationTriggers({
    page: currentPage,
    limit: pageSize
  });

  const createTriggerMutation = useCreateUserNotificationTrigger({
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKeys.getUserNotificationTriggers);
      setSnackbar({
        open: true,
        message: t('userNotificationTriggers.createSuccess'),
        severity: 'success'
      });
      setShowForm(false);
      setEditingTrigger(null);
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: t('userNotificationTriggers.saveError'),
        severity: 'error'
      });
    }
  });

  const updateTriggerMutation = useUpdateUserNotificationTrigger({
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKeys.getUserNotificationTriggers);
      setSnackbar({
        open: true,
        message: t('userNotificationTriggers.updateSuccess'),
        severity: 'success'
      });
      setShowForm(false);
      setEditingTrigger(null);
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: t('userNotificationTriggers.saveError'),
        severity: 'error'
      });
    }
  });

  const toggleTriggerMutation = useToggleUserNotificationTrigger({
    onMutate: async ({ id, enabled }) => {
      await queryClient.cancelQueries(QueryKeys.getUserNotificationTriggers);

      const previousQueries = queryClient.getQueriesData<PagedResponse<UserNotificationTrigger>>(
        QueryKeys.getUserNotificationTriggers
      );

      previousQueries.forEach(([queryKey, data]) => {
        if (!data) return;
        const updatedItems = data.items.map((item) =>
          item.id === id ? { ...item, enabled } : item
        );
        queryClient.setQueriesData(queryKey, { ...data, items: updatedItems });
      });

      return { previousQueries };
    },
    onSuccess: (updatedTrigger) => {
      queryClient.invalidateQueries(QueryKeys.getUserNotificationTriggers);
      setSnackbar({
        open: true,
        message: updatedTrigger.enabled 
          ? t('userNotificationTriggers.activatedSuccess')
          : t('userNotificationTriggers.deactivatedSuccess'),
        severity: 'success'
      });
      setToggleLoadingId(null);
    },
    onError: (_error, _variables, context) => {
      context?.previousQueries?.forEach(([queryKey, data]) => {
        queryClient.setQueriesData(queryKey, data);
      });
      setSnackbar({
        open: true,
        message: t('userNotificationTriggers.toggleError'),
        severity: 'error'
      });
      setToggleLoadingId(null);
    }
  });

  const deleteTriggerMutation = useDeleteUserNotificationTrigger({
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKeys.getUserNotificationTriggers);
      setSnackbar({
        open: true,
        message: t('userNotificationTriggers.deleteSuccess'),
        severity: 'success'
      });
      setDeletingTrigger(null);
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: t('userNotificationTriggers.deleteError'),
        severity: 'error'
      });
      setDeletingTrigger(null);
    }
  });

  const triggers = triggersData?.items || [];
  const totalPages = triggersData?.pages || 0;
  const currentTriggers = triggers;

  const handleCreateNew = () => {
    setEditingTrigger(null);
    setShowForm(true);
  };

  const handleEdit = (trigger: UserNotificationTrigger) => {
    setEditingTrigger(trigger);
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    const trigger = triggers.find(t => t.id === id);
    if (trigger) {
      setDeletingTrigger(trigger);
    }
  };

  const handleConfirmDelete = () => {
    if (deletingTrigger) {
      deleteTriggerMutation.mutate(deletingTrigger.id);
    }
  };

  const handleCancelDelete = () => {
    setDeletingTrigger(null);
  };

  const handleToggleActive = (id: string, isActive: boolean) => {
    // Prevenir spam de clicks
    if (toggleLoadingId === id) return;
    
    setToggleLoadingId(id);
    toggleTriggerMutation.mutate({ id, enabled: isActive });
  };

  const handleSubmit = async (data: UserNotificationTriggerFormData) => {
    if (editingTrigger) {
      // Update existing trigger
      updateTriggerMutation.mutate({ 
        id: editingTrigger.id, 
        data 
      });
    } else {
      // Create new trigger
      createTriggerMutation.mutate(data);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTrigger(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleBackToList = () => {
    setShowForm(false);
    setEditingTrigger(null);
  };

  // Mobile fullscreen dialog
  if (isMobile) {
    return (
      <>
        <Dialog
          fullScreen
          open={open}
          onClose={onClose}
          TransitionComponent={Transition}
        >
          <AppBar sx={{ position: 'relative' }}>
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={showForm ? handleBackToList : onClose}
              >
                {showForm ? <ArrowBack /> : <Close />}
              </IconButton>
              <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                {showForm
                  ? (editingTrigger
                    ? t('userNotificationTriggers.editTrigger')
                    : t('userNotificationTriggers.createTrigger'))
                  : t('userNotificationTriggers.title')
                }
              </Typography>
              {!showForm && (
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={handleCreateNew}
                  sx={{
                    height: theme.spacing(5),
                    minWidth: theme.spacing(5),
                    padding: theme.spacing(1),
                  }}
                >
                  <Add />
                </Button>
              )}
            </Toolbar>
          </AppBar>

          <DialogContent sx={{ overflow: 'visible' }}>
            {showForm ? (
              <Box p={3} pt={0}>
                <TriggerForm
                  trigger={editingTrigger || undefined}
                  deliveryCapabilities={deliveryCapabilities}
                  onSubmit={handleSubmit}
                  onCancel={handleCancel}
                  isLoading={createTriggerMutation.isLoading || updateTriggerMutation.isLoading}
                />
              </Box>
            ) : (
              <Box>
                {isLoading ? (
                  <Box p={3} textAlign="center">
                    <Typography variant="body1" color="text.secondary">
                      {t('common.loading')}...
                    </Typography>
                  </Box>
                ) : currentTriggers.length === 0 ? (
                  <Box p={3} textAlign="center">
                    <Typography variant="body1" color="text.secondary">
                      {t('userNotificationTriggers.noTriggers')}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <List sx={{ p: 0 }}>
                      {currentTriggers.map((trigger, index) => (
                        <TriggerListItem
                          key={trigger.id}
                          trigger={trigger}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                          onToggleActive={handleToggleActive}
                          index={index}
                          totalItems={currentTriggers.length}
                          variant="mobile"
                          isToggleLoading={toggleLoadingId === trigger.id}
                        />
                      ))}
                    </List>

                    {totalPages > 1 && (
                      <Box display="flex" justifyContent="center" p={3}>
                        <Pagination
                          count={totalPages}
                          page={currentPage}
                          onChange={(_, page) => handlePagination(page)}
                          color="primary"
                          disabled={isLoading}
                        />
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}
          </DialogContent>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        <ConfirmDeleteModal
          open={!!deletingTrigger}
          trigger={deletingTrigger}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          isLoading={deleteTriggerMutation.isLoading}
        />
      </>
    );
  }

  // Desktop modal
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        scroll="body"
        PaperProps={{
          sx: { 
            my: 2,
            mx: 'auto',
            maxHeight: 'none'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {showForm
                ? (editingTrigger
                  ? t('userNotificationTriggers.editTrigger')
                  : t('userNotificationTriggers.createTrigger'))
                : t('userNotificationTriggers.title')
              }
            </Typography>
            <Box display="flex" gap={1}>
              {!showForm && (
                <Button
                  variant="contained"
                  onClick={handleCreateNew}
                  aria-label="Create new"
                  sx={{
                    height: theme.spacing(5),
                    minWidth: theme.spacing(5),
                    padding: theme.spacing(1),
                  }}
                >
                  <Add />
                </Button>
              )}
              <IconButton onClick={showForm ? handleBackToList : onClose}>
                {showForm ? <ArrowBack /> : <Close />}
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, overflow: 'visible' }}>
          {showForm ? (
            <Box p={3} pt={0}>
              <TriggerForm
                trigger={editingTrigger || undefined}
                deliveryCapabilities={deliveryCapabilities}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                isLoading={createTriggerMutation.isLoading || updateTriggerMutation.isLoading}
              />
            </Box>
          ) : (
            <Box>
              {isLoading ? (
                <Box p={4} textAlign="center">
                  <Typography variant="h6" color="text.secondary">
                    {t('common.loading')}...
                  </Typography>
                </Box>
              ) : currentTriggers.length === 0 ? (
                <Box p={4} textAlign="center">
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t('userNotificationTriggers.noTriggers')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    {t('userNotificationTriggers.noTriggersDescription')}
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ p: 3 }}>
                    {currentTriggers.map((trigger, index) => (
                      <TriggerListItem
                        key={trigger.id}
                        trigger={trigger}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onToggleActive={handleToggleActive}
                        index={index}
                        totalItems={currentTriggers.length}
                        variant="desktop"
                        isToggleLoading={toggleLoadingId === trigger.id}
                      />
                    ))}
                  </Box>

                  {totalPages > 1 && (
                    <Box display="flex" justifyContent="center" p={3}>
                      <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(_, page) => handlePagination(page)}
                        color="primary"
                        disabled={isLoading}
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ConfirmDeleteModal
        open={!!deletingTrigger}
        trigger={deletingTrigger}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={deleteTriggerMutation.isLoading}
      />
    </>
  );
};
